import type {
  EquipmentCatalogEntry,
  OemElectricalMachineBasisSnapshot,
  OemGenerationBasisSnapshot,
} from '../../types/supplyChain';
import {
  OEM_GENERATION_SOURCE_REGISTER,
  OEM_GENERATION_SOURCE_REGISTER_DIGEST,
  assessOemProductFootprint,
  findOemGenerationProduct,
  oemElectricalMachineRecordDigest,
  oemGenerationProductRecordDigest,
  oemGenerationProductSourceIds,
} from '../generationImpact/generationOemSourceRegister';
import { assessOemSimulationAdmission } from '../generationImpact/generationOemSimulationAdmissions';
import { supplierIdsForOemManufacturer } from './oemGenerationSupplierLinks';

export function createOemGenerationBasisSnapshot(
  productId: string,
  performanceCaseId: string,
): OemGenerationBasisSnapshot | null {
  const product = findOemGenerationProduct(productId);
  const performance = product?.performanceCases.find(item => item.id === performanceCaseId);
  if (!product || !performance) return null;
  const footprint = assessOemProductFootprint(product);
  const admission = assessOemSimulationAdmission(productId, performanceCaseId);
  const admitted = admission.admissions.filter(item => item.status === 'apply-approved');
  return {
    registerSchemaVersion: OEM_GENERATION_SOURCE_REGISTER.schemaVersion,
    registerCompiledAt: OEM_GENERATION_SOURCE_REGISTER.compiledAt,
    registerDigest: OEM_GENERATION_SOURCE_REGISTER_DIGEST,
    recordDigest: oemGenerationProductRecordDigest(product),
    productId,
    performanceCaseId,
    manufacturer: product.manufacturer,
    model: product.model,
    variant: product.variant,
    technology: product.technology,
    packageScope: product.packageScope,
    electricalOutputKw: performance.electricalOutputKw,
    ratingScope: performance.ratingScope,
    frequencyHz: [...performance.frequencyHz],
    duty: performance.duty,
    sourceIds: [...oemGenerationProductSourceIds(product)],
    supplierIds: [...supplierIdsForOemManufacturer(product.manufacturer)],
    footprintAuthority: footprint.authority,
    physicalReferenceId: footprint.physicalReferenceId,
    authority: admitted.length > 0 ? 'simulation-admitted' : 'research-only',
    simulationAdmissionIds: admitted.map(item => item.id),
  };
}

export function createOemElectricalMachineBasisSnapshot(
  machineId: string,
  ratingCaseId: string,
): OemElectricalMachineBasisSnapshot | null {
  const machine = OEM_GENERATION_SOURCE_REGISTER.electricalMachines.find(
    item => item.id === machineId,
  );
  const rating = machine?.ratingCases.find(item => item.id === ratingCaseId);
  const recordDigest = oemElectricalMachineRecordDigest(machineId);
  if (!machine || !rating || !recordDigest) return null;
  const sourceIds = new Set(machine.identitySourceIds);
  rating.sourceIds.forEach(id => sourceIds.add(id));
  machine.studyCases
    .filter(item => item.ratingCaseId === ratingCaseId)
    .forEach(item => item.sourceIds.forEach(id => sourceIds.add(id)));
  return {
    registerSchemaVersion: OEM_GENERATION_SOURCE_REGISTER.schemaVersion,
    registerCompiledAt: OEM_GENERATION_SOURCE_REGISTER.compiledAt,
    registerDigest: OEM_GENERATION_SOURCE_REGISTER_DIGEST,
    recordDigest,
    machineId,
    ratingCaseId,
    manufacturer: machine.manufacturer,
    family: machine.family,
    model: machine.model,
    variant: machine.variant,
    ratedKva: rating.ratedKva,
    ratedVoltageKv: rating.ratedVoltageKv,
    frequencyHz: rating.frequencyHz,
    ratedPowerFactor: rating.ratedPowerFactor,
    poles: rating.poles,
    ratedSpeedRpm: rating.ratedSpeedRpm,
    sourceIds: [...sourceIds].sort(),
    supplierIds: [...supplierIdsForOemManufacturer(machine.manufacturer)],
    authority: 'research-only',
  };
}

export function oemBasisFromEquipmentCatalogEntry(entry: EquipmentCatalogEntry): {
  generation?: OemGenerationBasisSnapshot;
  electricalMachine?: OemElectricalMachineBasisSnapshot;
} {
  const generation = entry.oemGenerationProductId && entry.oemPerformanceCaseId
    ? createOemGenerationBasisSnapshot(
      entry.oemGenerationProductId,
      entry.oemPerformanceCaseId,
    ) ?? undefined
    : undefined;
  const electricalMachine = entry.oemElectricalMachineId && entry.oemElectricalRatingCaseId
    ? createOemElectricalMachineBasisSnapshot(
      entry.oemElectricalMachineId,
      entry.oemElectricalRatingCaseId,
    ) ?? undefined
    : undefined;
  return { generation, electricalMachine };
}
